import { get } from 'lodash-es';
import PropTypes from 'prop-types';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { EdmundsAPI } from 'client/data/api/api-client';
import { withMetrics } from 'client/data/api/api-metrics';

export function buildBuyingTipsPath({ make, model, year }) {
  return `makes["${make}"].models["${model}"].years["${year}"].buyingTipsSummary`;
}

/**
 * Converts **text** to <strong>text</strong>
 * @param {string} text - Text that may contain **bold** markers
 * @returns {string} Text with bold markers converted to <strong> tags
 */
function convertBoldText(text) {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

/**
 * Transforms buying tips paragraph format from newline-separated bullets
 * to HTML with <p>, <ul>, and <li> tags
 *
 * Expected input format:
 * "Intro paragraph text.\n\n- First bullet point\n\n- Second bullet point"
 *
 * Output format:
 * "<p>Intro paragraph text.</p><ul><li>First bullet point</li><li>Second bullet point</li></ul>"
 *
 * Also converts **bold text** to <strong>bold text</strong>
 *
 * @param {string} paragraph - Raw paragraph with \n\n- separated bullets
 * @returns {string} HTML formatted text
 */
export function formatBuyingTipsParagraph(paragraph) {
  if (!paragraph) return null;

  // Split by lines, preserving structure
  const lines = paragraph.split('\n');

  let html = '';
  let inBulletList = false;
  let currentParagraph = '';

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].trim();

    // Process non-empty lines
    if (line) {
      // Check if this is a bullet point
      if (line.startsWith('-')) {
        // Close any open paragraph
        if (currentParagraph) {
          html += `<p>${convertBoldText(currentParagraph.trim())}</p>`;
          currentParagraph = '';
        }

        // Open bullet list if not already open
        if (!inBulletList) {
          html += '<ul>';
          inBulletList = true;
        }

        // Add bullet item (remove leading '-' and whitespace)
        const bulletContent = line.substring(1).trim();
        html += `<li>${convertBoldText(bulletContent)}</li>`;
      } else if (inBulletList) {
        // This is regular text and we're in a bullet list
        // Close the bullet list
        html += '</ul>';
        inBulletList = false;
        // Start new paragraph
        currentParagraph = line;
      } else if (currentParagraph) {
        // This is regular text and we're not in a bullet list
        // Continue building paragraph
        currentParagraph += ` ${line}`;
      } else {
        currentParagraph = line;
      }
    }
  }

  // Close any remaining open elements
  if (currentParagraph) {
    html += `<p>${convertBoldText(currentParagraph.trim())}</p>`;
  }
  if (inBulletList) {
    html += '</ul>';
  }

  return html;
}

export const BuyingTipsEntities = {
  BuyingTipsSummary: PropTypes.shape({
    paragraph: PropTypes.string,
    created: PropTypes.string,
    formattedText: PropTypes.string,
    median_monthly_lease_payment_model: PropTypes.number,
    median_primary_customer_cash_model: PropTypes.number,
    median_apr_special_60: PropTypes.number,
    edmunds_calc_savings_off_msrp_model: PropTypes.number,
  }),
};

export const BuyingTipsModel = createModelSegment('buyingTips', [
  /**
   * Example: https://qa-11-www.edmunds.com/api/cr-summaries/v1/buying-tips/nissan/sentra/2025
   * @see buildBuyingTipsPath
   */
  {
    path: 'makes["{makeSlug}"].models["{modelSlug}"].years["{year}"].buyingTipsSummary',
    resolve({ makeSlug, modelSlug, year }, context) {
      return withMetrics(EdmundsAPI, context)
        .fetchJson(`/cr-summaries/v1/buying-tips/${makeSlug}/${modelSlug}/${year}`)
        .then(response => {
          const results = get(response, 'results', null);

          if (!results || !results.paragraph) return null;

          return {
            ...results,
            formattedText: formatBuyingTipsParagraph(results.paragraph),
          };
        })
        .catch(() => null);
    },
  },
]);
